import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../hooks/useTheme';

// Admin usage dashboard at #/admin. Reads GET /admin/stats from the party
// Worker (party/src/adminStats.ts), authenticated with the ADMIN_TOKEN
// secret. The token is kept in localStorage so this browser stays signed in
// until "Sign out".

const PARTYKIT_HOST = import.meta.env.VITE_PARTYKIT_HOST;
const TOKEN_KEY = 'plate-seeker-admin-token';
type Range = number | 'all';
const RANGE_OPTIONS: Range[] = [7, 30, 90, 365, 'all'];

interface AdminStats {
  generatedAt: string;
  days: Range;
  totals: {
    games: number;
    demoGames: number;
    completed: number;
    abandoned: number;
    inProgress: number;
    uniquePlayers: number;
    playerSessions: number;
  };
  daily: { day: string; games: number; uniquePlayers: number }[];
  countries: { country: string; uniquePlayers: number; playerSessions: number }[];
  topPlayers: { playerName: string; games: number; wins: number; lastPlayed: string }[];
  recentGames: {
    id: string;
    roomCode: string;
    isDemo: boolean;
    status: string;
    startedAt: string;
    endedAt: string | null;
    winnerName: string | null;
    players: { playerName: string; isComputer: boolean; country: string | null; finalScore: number; isWinner: boolean }[];
  }[];
}

function statsUrl(days: Range): string {
  const isLocal = PARTYKIT_HOST.startsWith('localhost') || PARTYKIT_HOST.startsWith('127.0.0.1');
  return `${isLocal ? 'http' : 'https'}://${PARTYKIT_HOST}/admin/stats?days=${days}`;
}

function readToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — stay signed in for this page load only */
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return '—';
  const mins = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60_000);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const label = 'text-xs font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark';
const card = 'bg-atlas-surface dark:bg-atlas-surface-dark rounded-xl';
const ink = 'text-atlas-ink dark:text-atlas-ink-dark';
const soft = 'text-atlas-ink-soft dark:text-atlas-ink-soft-dark';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <span className={label}>{title}</span>
      {children}
    </section>
  );
}

function Stat({ value, caption }: { value: number; caption: string }) {
  return (
    <div className={`${card} px-4 py-3 flex flex-col`}>
      <span className={`font-mono text-2xl font-semibold ${ink}`}>{value.toLocaleString()}</span>
      <span className={`text-xs ${soft}`}>{caption}</span>
    </div>
  );
}

function StatusPill({ status, isDemo }: { status: string; isDemo: boolean }) {
  const color =
    status === 'completed'
      ? 'text-atlas-pine dark:text-atlas-pine-dark'
      : status === 'abandoned'
        ? 'text-atlas-brick dark:text-atlas-brick-dark'
        : 'text-atlas-brass dark:text-atlas-brass-dark';
  return (
    <span className={`text-xs font-semibold uppercase tracking-wide ${color}`}>
      {status === 'playing' ? 'in progress' : status}
      {isDemo && <span className={`ml-1.5 font-normal normal-case ${soft}`}>· demo</span>}
    </span>
  );
}

function SignIn({ onSubmit, error }: { onSubmit: (token: string) => void; error: string }) {
  const [value, setValue] = useState('');
  return (
    <form
      className="flex flex-col gap-3 max-w-sm mx-auto w-full pt-10"
      onSubmit={e => {
        e.preventDefault();
        if (value.trim()) onSubmit(value.trim());
      }}
    >
      <span className={`font-serif text-2xl font-bold ${ink}`}>Admin sign in</span>
      <input
        type="password"
        autoComplete="current-password"
        placeholder="Admin token"
        value={value}
        onChange={e => setValue(e.target.value)}
        className={`${card} ${ink} px-3.5 py-3 border border-atlas-rule dark:border-atlas-rule-dark focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark`}
      />
      {error && <span className="text-sm text-atlas-brick dark:text-atlas-brick-dark">{error}</span>}
      <button
        type="submit"
        className="min-h-[44px] rounded-xl bg-atlas-ink dark:bg-atlas-ink-dark text-atlas-bg dark:text-atlas-bg-dark font-semibold"
      >
        Sign in
      </button>
    </form>
  );
}

function Dashboard({ stats }: { stats: AdminStats }) {
  const maxDaily = Math.max(1, ...stats.daily.map(d => d.games));
  const humanSessions = (g: AdminStats['recentGames'][number]) => g.players.filter(p => !p.isComputer);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat value={stats.totals.uniquePlayers} caption="unique players" />
        <Stat value={stats.totals.games} caption={`games (${stats.totals.demoGames} demo)`} />
        <Stat value={stats.totals.completed} caption="completed" />
        <Stat value={stats.totals.abandoned} caption="abandoned" />
      </div>
      <p className={`text-xs ${soft} -mt-4`}>
        Unique players are counted by hashed IP, so people sharing a network count once. Only games that started
        are logged — lobby visits aren't. {stats.totals.inProgress > 0 && `${stats.totals.inProgress} game(s) still in progress or never closed.`}
      </p>

      <Section title="Games per day">
        {stats.daily.length === 0 ? (
          <span className={`text-sm ${soft}`}>No games in this period.</span>
        ) : (
          <div className={`${card} px-4 py-3 flex flex-col gap-1.5`}>
            {stats.daily.map(d => (
              <div key={d.day} className="flex items-center gap-3 text-sm">
                <span className={`font-mono w-[86px] shrink-0 ${soft}`}>{d.day}</span>
                <div className="flex-1 h-3 rounded-full bg-atlas-bg dark:bg-atlas-bg-dark overflow-hidden">
                  <div
                    className="h-full rounded-full bg-atlas-brass dark:bg-atlas-brass-dark"
                    style={{ width: `${(d.games / maxDaily) * 100}%` }}
                  />
                </div>
                <span className={`font-mono w-[92px] shrink-0 text-right ${ink}`}>
                  {d.games}g · {d.uniquePlayers}p
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="grid gap-6 sm:grid-cols-2">
        <Section title="Countries">
          <div className={`${card} divide-y divide-atlas-rule dark:divide-atlas-rule-dark`}>
            {stats.countries.length === 0 && <div className={`px-4 py-3 text-sm ${soft}`}>No data.</div>}
            {stats.countries.map(c => (
              <div key={c.country} className="flex justify-between px-4 py-2 text-sm">
                <span className={`font-mono ${ink}`}>{c.country}</span>
                <span className={soft}>
                  {c.uniquePlayers} players · {c.playerSessions} plays
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Top player names">
          <div className={`${card} divide-y divide-atlas-rule dark:divide-atlas-rule-dark`}>
            {stats.topPlayers.length === 0 && <div className={`px-4 py-3 text-sm ${soft}`}>No data.</div>}
            {stats.topPlayers.map(p => (
              <div key={p.playerName} className="flex justify-between gap-3 px-4 py-2 text-sm">
                <span className={`truncate font-semibold ${ink}`}>{p.playerName}</span>
                <span className={`shrink-0 ${soft}`}>
                  {p.games} game{p.games === 1 ? '' : 's'} · {p.wins} win{p.wins === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section title={`Recent games (last ${stats.recentGames.length}, any date)`}>
        <div className="flex flex-col gap-2">
          {stats.recentGames.map(g => (
            <div key={g.id} className={`${card} px-4 py-3 flex flex-col gap-1.5`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className={`font-mono font-semibold ${ink}`}>{g.roomCode}</span>
                <StatusPill status={g.status} isDemo={g.isDemo} />
              </div>
              <span className={`text-xs ${soft}`}>
                {formatDateTime(g.startedAt)} · {formatDuration(g.startedAt, g.endedAt)} ·{' '}
                {humanSessions(g).length} human player{humanSessions(g).length === 1 ? '' : 's'}
              </span>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {g.players.map((p, i) => (
                  <span key={i} className={p.isComputer ? soft : ink}>
                    {p.isWinner && '🏆 '}
                    {p.playerName}
                    {p.isComputer && ' (CPU)'}
                    {p.country && <span className={`ml-1 font-mono text-xs ${soft}`}>{p.country}</span>}
                    <span className="ml-1 font-mono text-xs text-atlas-brass dark:text-atlas-brass-dark">{p.finalScore}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <span className={`text-xs ${soft}`}>Generated {formatDateTime(stats.generatedAt)}</span>
    </div>
  );
}

export function AdminPage() {
  const { isDark, toggle } = useTheme();
  const [token, setToken] = useState(readToken);
  const [days, setDays] = useState<Range>(30);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const signOut = useCallback(() => {
    writeToken(null);
    setToken('');
    setStats(null);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(statsUrl(days), { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        signOut();
        setError('That token was rejected.');
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      writeToken(token);
      setStats(body as AdminStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [token, days, signOut]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-dvh w-full bg-atlas-bg dark:bg-atlas-bg-dark">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
        <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark flex flex-wrap items-center justify-between gap-x-2 pt-[calc(env(safe-area-inset-top)+14px)] pb-2">
          <span className={`font-serif text-xl font-bold whitespace-nowrap ${ink}`}>Plate Seeker · Admin</span>
          <div className="flex items-center gap-1 ml-auto">
            {token && (
              <>
                <select
                  value={days}
                  onChange={e => setDays(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  aria-label="Time range"
                  className={`${card} ${ink} text-sm px-2 py-1.5 border border-atlas-rule dark:border-atlas-rule-dark`}
                >
                  {RANGE_OPTIONS.map(d => (
                    <option key={d} value={d}>
                      {d === 'all' ? 'All time' : `Last ${d} days`}
                    </option>
                  ))}
                </select>
                <button onClick={() => void load()} disabled={loading} className={`text-sm px-2.5 min-h-[44px] ${ink} disabled:opacity-50`}>
                  {loading ? 'Loading…' : 'Refresh'}
                </button>
                <button onClick={signOut} className={`text-sm px-2.5 min-h-[44px] ${soft}`}>
                  Sign out
                </button>
              </>
            )}
            <ThemeToggle isDark={isDark} onToggle={toggle} />
          </div>
        </div>

        {!token ? (
          <SignIn onSubmit={setToken} error={error} />
        ) : (
          <div className="flex flex-col gap-4 pt-2">
            {error && <span className="text-sm text-atlas-brick dark:text-atlas-brick-dark">{error}</span>}
            {stats ? <Dashboard stats={stats} /> : loading && <span className={`text-sm ${soft}`}>Loading…</span>}
          </div>
        )}
      </div>
    </div>
  );
}
